import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { StoreErrorHandler } from '@zero/shared/util-global-state';
import { OrderStatus } from '@zero/shared/util-models';
import { TradingTimesService } from '@zero/trade/domain';
import { catchError, filter, finalize, map, of, switchMap, takeUntil, takeWhile, tap, timer } from 'rxjs';
import { EditOrderInfrastructure } from '../../infrastructure/edit-order.infrastructure';
import { editOrderActions, editOrderApiActions } from './edit-order.actions';
import { editOrderFeature } from './edit-order.reducers';

@Injectable({ providedIn: 'root' })
export class EditOrderEffects {
  readonly #actions = inject(Actions);
  readonly #store = inject(Store);
  readonly #editOrderInfrastructure = inject(EditOrderInfrastructure);
  readonly #tradingTimesService = inject(TradingTimesService);
  readonly #storeErrorHandler = inject(StoreErrorHandler);

  public checkTradingTimes$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(editOrderActions.checkTradingTimes),
      switchMap(({ isin }) =>
        this.#editOrderInfrastructure.getAsset(isin).pipe(
          map((asset) =>
            editOrderApiActions.checkTradingTimesSuccess({
              isOutOfTradingTimes: !this.#tradingTimesService.isActiveTradingHours(new Date(), asset),
            }),
          ),
          catchError((error) => of(editOrderApiActions.checkTradingTimesFailure({ error }))),
        ),
      ),
    );
  });

  public cancelOrder$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(editOrderActions.cancelOrder),
      switchMap(({ orderId }) =>
        this.#editOrderInfrastructure.cancelOrder(orderId).pipe(
          map(() => editOrderApiActions.cancelOrderSuccess()),
          catchError((error) => of(editOrderApiActions.cancelOrderFailure({ error }))),
        ),
      ),
    );
  });

  public checkCancelState$ = createEffect(() => {
    const interval = 2000;
    const maxTime = 1 * 60 * 1000;

    return this.#actions.pipe(
      ofType(editOrderActions.checkCancelState),
      switchMap(() =>
        timer(0, interval).pipe(
          concatLatestFrom(() => this.#store.select(editOrderFeature.selectIsCancellationDone)),
          takeWhile(([, isCancellationDone]) => !isCancellationDone),
          takeUntil(timer(maxTime)),
          finalize(() => this.#store.dispatch(editOrderActions.resetTimer())),
        ),
      ),
      concatLatestFrom(() => this.#store.select(editOrderFeature.selectOrderId)),
      filter(([, orderId]) => !!orderId),
      switchMap(([, orderId]) =>
        this.#editOrderInfrastructure.checkOrder(orderId).pipe(
          map(({ status }) =>
            editOrderApiActions.checkCancelStateSuccess({
              isCancellationDone: [OrderStatus.Canceled, OrderStatus.Rejected].includes(status),
            }),
          ),
          catchError((error) => of(editOrderApiActions.checkCancelStateFailure({ error }))),
        ),
      ),
    );
  });

  public orderCancelTriggered$ = createEffect(() => {
    return this.#store.select(editOrderFeature.selectIsCancellationTriggered).pipe(
      filter((isTriggered) => isTriggered),
      map(() => editOrderActions.checkCancelState()),
    );
  });

  public handleBackendErrors$ = createEffect(
    () => {
      return this.#actions.pipe(
        ofType(
          editOrderApiActions.cancelOrderFailure,
          editOrderApiActions.checkCancelStateFailure,
          editOrderApiActions.checkTradingTimesFailure,
        ),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
